import { redirect, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, useSearchParams } from '@remix-run/react'
import { Button } from '#app/components/ui/button.tsx'
import { VideoComponent } from '#app/components/video.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { createToastHeaders } from '#app/utils/toast.server.ts'

const PAGE_ITEMS = 12

export async function loader({ request, params }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const page = Number(url.searchParams.get('page')) || 1

	const channelTitle = params.channel
	const channel = await prisma.channel.findFirstOrThrow({
		where: {
			title: channelTitle,
		},
	})

	const videos = await prisma.video.findMany({
		where: {
			channel,
		},
		include: {
			artist: true,
		},
		skip: (page - 1) * PAGE_ITEMS,
		take: PAGE_ITEMS,
		orderBy: {
			publishedAt: 'desc',
		},
	})

	if (!videos.length) {
		const headers = await createToastHeaders({
			title: 'Error',
			description: "Couldn't find those videos",
		})
		return redirect('/', { headers })
	}

	const totalVideoCount = await prisma.video.count({
		where: {
			channel,
		},
	})

	const lastPage = 1 + Math.floor(totalVideoCount / PAGE_ITEMS)

	return {
		channel,
		videos,
		lastPage,
	}
}

export default function VideosPage() {
	const { channel, videos, lastPage } = useLoaderData<typeof loader>()

	const [searchParams] = useSearchParams()
	const currentPage = Number(searchParams.get('page')) || 1
	const nextPage = currentPage + 1

	return (
		<main className="container mb-12 mt-2 flex max-w-screen-lg flex-col justify-center">
			<h1 className="mb-6 inline text-h2">{channel.title} videos</h1>
			<ul className="grid grid-flow-row-dense gap-8 sm:grid-cols-2 lg:grid-cols-3">
				{videos.map(({ artist, ...video }) => (
					<VideoComponent key={video.youtubeId} video={video} artist={artist} />
				))}
			</ul>
			<div
				className={cn('flex items-center justify-center', {
					hidden: nextPage > lastPage,
				})}
			>
				<Button asChild className="mt-8">
					<Link to={`?page=${nextPage}`}>Show More</Link>
				</Button>
			</div>
		</main>
	)
}
